/**
 * Meta Ads — panel EN DIRECTO de salud de campañas Facebook/Instagram Ads.
 * Auto-refresh cada 5 minutos. Indicador de "última actualización".
 * Muestra: KPIs (hoy vs ayer), tabla ads activos con estado real, alertas, campañas.
 *
 * Requiere META_ACCESS_TOKEN + META_AD_ACCOUNT_ID en env del VPS.
 */
import { h, apiClient, formatEur, btn, toast, emptyState, skeletonGrid, openModal, pill } from './_common.js';

// Estado del módulo (auto-refresh)
let _liveRefreshTimer = null;
let _agoTickTimer = null;
let _lastDataTs = null;
let _currentRoot = null;

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 min

function stopTimers() {
  if (_liveRefreshTimer) { clearInterval(_liveRefreshTimer); _liveRefreshTimer = null; }
  if (_agoTickTimer) { clearInterval(_agoTickTimer); _agoTickTimer = null; }
}

function agoLabel(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5) return 'ahora mismo';
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff/60)} min`;
  return `hace ${Math.floor(diff/3600)}h`;
}

function deltaPct(now, prev) {
  if (!prev || prev === 0) return now > 0 ? '+∞%' : '—';
  const d = ((now - prev) / prev) * 100;
  return (d >= 0 ? '+' : '') + d.toFixed(0) + '%';
}

function deltaColor(now, prev, higherIsBetter = true) {
  if (!prev) return 'text-ink-300';
  const d = now - prev;
  if (Math.abs(d) < 0.01) return 'text-ink-300';
  const better = higherIsBetter ? d > 0 : d < 0;
  return better ? 'text-emerald-300' : 'text-red-300';
}

export async function renderMetaAds(root) {
  stopTimers();
  _currentRoot = root;
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-7xl mx-auto px-6 py-6 space-y-6 animate-fade-in' });
  root.appendChild(wrap);

  // Header con timestamp + refresh
  const tsLabel = h('span', { class: 'text-xs text-ink-300', id: 'meta-ads-ago' }, 'Cargando...');
  const refreshBtn = btn('🔄 Actualizar', () => refreshLive(root, true));
  wrap.appendChild(h('div', { class: 'flex items-center justify-between' }, [
    h('div', {}, [
      h('h2', { class: 'font-display text-3xl font-bold' }, '📣 Meta Ads · En directo'),
      h('p', { class: 'text-sm text-ink-300 flex items-center gap-2' }, [
        h('span', { class: 'inline-flex items-center gap-1' }, [
          h('span', { class: 'w-2 h-2 rounded-full bg-emerald-400 animate-pulse' }),
          'Live · auto-refresh 5 min · ',
        ]),
        tsLabel,
      ]),
    ]),
    h('div', { class: 'flex gap-2' }, [
      btn('Configurar API', () => openConfigModal(root), { variant: 'secondary' }),
      refreshBtn,
    ]),
  ]));

  // Quick links
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    const qlContainer = h('div');
    wrap.appendChild(qlContainer);
    await renderQuickLinks(qlContainer, 'meta-ads', {
      title: '🔗 Mis cuentas Meta',
      subtitle: 'Business Manager, Ads Manager, Pixel, WhatsApp Business — acceso directo.',
      onReload: () => renderMetaAds(root),
    });
  } catch { /* skip */ }

  // Contenedor del live panel
  const content = h('div', { id: 'meta-ads-live-content' });
  wrap.appendChild(content);
  content.appendChild(skeletonGrid(4, 4));

  // Primera carga
  await refreshLive(root, false);

  // Iniciar auto-refresh
  _liveRefreshTimer = setInterval(() => refreshLive(root, false), AUTO_REFRESH_MS);
  _agoTickTimer = setInterval(() => {
    const el = document.getElementById('meta-ads-ago');
    if (el && _lastDataTs) el.textContent = `Última actualización: ${agoLabel(_lastDataTs)}`;
  }, 15000);
}

async function refreshLive(root, force) {
  const content = root.querySelector('#meta-ads-live-content');
  if (!content) return;
  try {
    const data = await apiClient.get(`/api/v1/meta-ads/live${force ? '?force=1' : ''}`);
    _lastDataTs = data.lastUpdated;
    const el = document.getElementById('meta-ads-ago');
    if (el) el.textContent = `Última actualización: ${agoLabel(_lastDataTs)}${data.cached ? ' · (caché 60s)' : ''}`;
    content.innerHTML = '';
    if (data.error) {
      content.appendChild(renderNotConfigured(root, data.error));
      return;
    }
    renderLivePanel(content, data, root);
    if (force) toast('Datos actualizados ✓', 'success', 2000);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error cargando', e?.message || 'Reintenta', btn('Reintentar', () => refreshLive(root, true))));
  }
}

function renderLivePanel(content, data, root) {
  const s = data.summary || {};

  // === KPIs principales (hoy + comparativa ayer) ===
  content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
    kpiCard('Gasto HOY', formatEur(s.spendToday || 0), 'text-gold-300', 'vs ayer ' + formatEur(s.spendYesterday || 0), deltaPct(s.spendToday || 0, s.spendYesterday || 0)),
    kpiCard('Conversiones HOY', String(s.conversionsToday || 0), 'text-emerald-300', `${s.conversions7d || 0} en 7 días`, ''),
    kpiCard('Clics HOY', (s.clicksToday || 0).toLocaleString(), 'text-sky-300', `${(s.impressionsToday || 0).toLocaleString()} impresiones`, ''),
    kpiCard('Spend 30 días', formatEur(s.spend30d || 0), 'text-ink-100', `7d: ${formatEur(s.spend7d || 0)}`, ''),
  ]));

  // === Estado de los anuncios ===
  content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, [
    statusBadge('🟢 Activos', s.activeAds || 0, 'emerald'),
    statusBadge('🟡 En revisión', s.inReviewAds || 0, 'amber'),
    statusBadge('🔴 Con problemas', s.withIssuesAds || 0, 'red'),
    statusBadge('⏸️ Pausados', s.pausedAds || 0, 'gray'),
  ]));

  // === Alertas ===
  if (data.alerts && data.alerts.length > 0) {
    const critical = data.alerts.filter((a) => a.severity === 'critical');
    const warnings = data.alerts.filter((a) => a.severity === 'warning');
    const info = data.alerts.filter((a) => a.severity === 'info');
    content.appendChild(h('h3', { class: 'font-display font-semibold mb-2 mt-4' }, `🚨 Alertas (${data.alerts.length})`));
    const alertGrid = h('div', { class: 'space-y-2' });
    [...critical, ...warnings, ...info].forEach((a) => alertGrid.appendChild(buildAlertCard(a)));
    content.appendChild(alertGrid);
  }

  // === Tabla anuncios activos ===
  content.appendChild(h('h3', { class: 'font-display font-semibold mb-2 mt-4' }, `📺 Tus anuncios (${(data.ads || []).length})`));
  if (data.ads && data.ads.length > 0) {
    content.appendChild(buildAdsTable(data.ads));
  } else {
    content.appendChild(emptyState('📣', 'Sin anuncios todavía', 'Cuando lances campañas Meta Ads, aparecerán aquí.', null));
  }

  // === Campañas (resumen) ===
  if (data.campaigns && data.campaigns.length > 0) {
    content.appendChild(h('h3', { class: 'font-display font-semibold mb-2 mt-4' }, `📊 Campañas (${data.campaigns.length})`));
    content.appendChild(buildCampaignsTable(data.campaigns));
  }
}

function kpiCard(label, value, color, sub, deltaText) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'text-xs uppercase tracking-wider text-ink-300 mb-1' }, label),
    h('div', { class: `text-2xl font-display font-bold ${color}` }, value),
    h('div', { class: 'flex items-center gap-2 mt-1' }, [
      h('span', { class: 'text-xs text-ink-300' }, sub),
      deltaText ? h('span', { class: `text-xs font-semibold ${deltaText.startsWith('+') ? 'text-emerald-300' : 'text-red-300'}` }, deltaText) : null,
    ]),
  ]);
}

function statusBadge(label, count, color) {
  const colorMap = {
    emerald: 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300',
    amber:   'bg-amber-500/10 border-amber-400/40 text-amber-300',
    red:     'bg-red-500/10 border-red-400/40 text-red-300',
    gray:    'bg-base-700/50 border-base-600 text-ink-300',
  };
  return h('div', { class: `border rounded-xl p-4 ${colorMap[color]}` }, [
    h('div', { class: 'text-sm font-semibold' }, label),
    h('div', { class: 'text-3xl font-display font-bold mt-1' }, String(count)),
  ]);
}

function buildAdsTable(ads) {
  // Ordena: con issues primero, luego activos, luego paused
  const order = { WITH_ISSUES: 0, DISAPPROVED: 0, PENDING_REVIEW: 1, IN_PROCESS: 1, ACTIVE: 2, PAUSED: 3, CAMPAIGN_PAUSED: 4, ADSET_PAUSED: 4 };
  const sorted = [...ads].sort((a, b) => (order[a.effectiveStatus] ?? 5) - (order[b.effectiveStatus] ?? 5) || (b.spend || 0) - (a.spend || 0));
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl overflow-hidden' }, [
    h('div', { class: 'overflow-x-auto' }, [
      h('table', { class: 'w-full text-sm' }, [
        h('thead', { class: 'bg-base-700/50' }, [
          h('tr', {}, [
            h('th', { class: 'text-left p-3' }, 'Anuncio'),
            h('th', { class: 'text-center p-3' }, 'Estado'),
            h('th', { class: 'text-right p-3' }, 'Spend 30d'),
            h('th', { class: 'text-right p-3' }, 'Impr.'),
            h('th', { class: 'text-right p-3' }, 'CTR'),
            h('th', { class: 'text-right p-3' }, 'CPC'),
            h('th', { class: 'text-right p-3' }, 'Freq.'),
            h('th', { class: 'text-right p-3' }, 'Conv.'),
            h('th', { class: 'text-right p-3' }, 'CPA'),
          ]),
        ]),
        h('tbody', {},
          sorted.map((a) => h('tr', { class: 'border-t border-base-700' }, [
            h('td', { class: 'p-3' }, [
              h('div', { class: 'font-medium' }, a.name || a.id),
              h('div', { class: 'text-xs text-ink-300' }, `${a.campaignName || '?'} → ${a.adsetName || '?'}`),
            ]),
            h('td', { class: 'p-3 text-center' }, effectiveStatusPill(a.effectiveStatus)),
            h('td', { class: 'p-3 text-right' }, formatEur(a.spend || 0)),
            h('td', { class: 'p-3 text-right text-ink-300' }, (a.impressions || 0).toLocaleString()),
            h('td', { class: 'p-3 text-right' }, a.ctr ? a.ctr.toFixed(2) + '%' : '—'),
            h('td', { class: 'p-3 text-right' }, a.cpc ? formatEur(a.cpc) : '—'),
            h('td', { class: 'p-3 text-right' }, a.frequency ? a.frequency.toFixed(1) : '—'),
            h('td', { class: 'p-3 text-right' }, String(a.conversions || 0)),
            h('td', { class: 'p-3 text-right' }, a.cpa ? formatEur(a.cpa) : '—'),
          ]))),
      ]),
    ]),
  ]);
}

function effectiveStatusPill(s) {
  const map = {
    ACTIVE:           { label: 'Activo', color: 'green' },
    PAUSED:           { label: 'Pausado', color: 'gray' },
    PENDING_REVIEW:   { label: 'En revisión', color: 'amber' },
    IN_PROCESS:       { label: 'Procesando', color: 'amber' },
    WITH_ISSUES:      { label: 'Con problema', color: 'red' },
    DISAPPROVED:      { label: 'Rechazado', color: 'red' },
    CAMPAIGN_PAUSED:  { label: 'Camp. pausada', color: 'gray' },
    ADSET_PAUSED:     { label: 'AdSet pausado', color: 'gray' },
    DELETED:          { label: 'Borrado', color: 'gray' },
    ARCHIVED:         { label: 'Archivado', color: 'gray' },
  };
  const m = map[s] || { label: s || 'unknown', color: 'gray' };
  return pill(m.label, m.color);
}

function buildAlertCard(a) {
  const colors = {
    critical: { bg: 'bg-red-500/10', border: 'border-red-400/40', text: 'text-red-300', emoji: '🔴' },
    warning:  { bg: 'bg-amber-500/10', border: 'border-amber-400/40', text: 'text-amber-300', emoji: '🟡' },
    info:     { bg: 'bg-sky-500/10', border: 'border-sky-400/40', text: 'text-sky-300', emoji: 'ℹ️' },
  };
  const c = colors[a.severity] || colors.info;
  return h('div', { class: `${c.bg} ${c.border} border rounded-lg p-3 flex items-start gap-3` }, [
    h('div', { class: 'text-2xl' }, c.emoji),
    h('div', { class: 'flex-1' }, [
      h('div', { class: `font-semibold text-sm ${c.text}` }, a.type || 'Alerta'),
      h('p', { class: 'text-sm text-ink-200 mt-0.5' }, a.message || ''),
      a.campaignName ? h('p', { class: 'text-xs text-ink-300 mt-1' }, `Campaña: ${a.campaignName}`) : null,
      a.suggestion ? h('p', { class: 'text-xs text-emerald-300 mt-1' }, '💡 ' + a.suggestion) : null,
    ]),
  ]);
}

function buildCampaignsTable(campaigns) {
  const sorted = [...campaigns].sort((a, b) => (b.spend || 0) - (a.spend || 0));
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl overflow-hidden' }, [
    h('div', { class: 'overflow-x-auto' }, [
      h('table', { class: 'w-full text-sm' }, [
        h('thead', { class: 'bg-base-700/50' }, [
          h('tr', {}, [
            h('th', { class: 'text-left p-3' }, 'Campaña'),
            h('th', { class: 'text-right p-3' }, 'Spend'),
            h('th', { class: 'text-right p-3' }, 'CTR'),
            h('th', { class: 'text-right p-3' }, 'CPC'),
            h('th', { class: 'text-right p-3' }, 'Conv.'),
            h('th', { class: 'text-right p-3' }, 'CPA'),
            h('th', { class: 'text-center p-3' }, 'Estado'),
          ]),
        ]),
        h('tbody', {},
          sorted.map((c) => h('tr', { class: 'border-t border-base-700' }, [
            h('td', { class: 'p-3' }, [
              h('div', { class: 'font-medium' }, c.name || c.id),
              c.objective ? h('div', { class: 'text-xs text-ink-300' }, c.objective) : null,
            ]),
            h('td', { class: 'p-3 text-right' }, formatEur(c.spend || 0)),
            h('td', { class: 'p-3 text-right' }, c.ctr ? c.ctr.toFixed(2) + '%' : '—'),
            h('td', { class: 'p-3 text-right' }, c.cpc ? formatEur(c.cpc) : '—'),
            h('td', { class: 'p-3 text-right' }, String(c.conversions || 0)),
            h('td', { class: 'p-3 text-right' }, c.cpa ? formatEur(c.cpa) : '—'),
            h('td', { class: 'p-3 text-center' }, pill(c.status || 'unknown', c.status === 'ACTIVE' ? 'green' : 'gray')),
          ]))),
      ]),
    ]),
  ]);
}

function renderNotConfigured(root, errorMsg) {
  return h('div', { class: 'bg-amber-500/10 border border-amber-400/40 rounded-xl p-6' }, [
    h('div', { class: 'flex items-start gap-4' }, [
      h('div', { class: 'text-4xl' }, '⚠️'),
      h('div', { class: 'flex-1' }, [
        h('h3', { class: 'font-display font-semibold text-lg text-amber-300' }, 'Meta Ads API no configurada'),
        h('p', { class: 'text-sm text-ink-300 mt-2' },
          'Para monitorizar tus campañas, necesito conectar tu Meta Business API.'),
        errorMsg ? h('p', { class: 'text-xs text-red-300 mt-3 font-mono' }, 'Error: ' + errorMsg) : null,
        h('div', { class: 'flex gap-2 mt-4' }, [
          h('a', { href: 'https://developers.facebook.com/apps/', target: '_blank', rel: 'noopener',
                   class: 'bg-gold-400 hover:bg-gold-300 text-base-900 px-4 py-2 rounded-lg text-sm font-semibold' }, '→ Crear Meta App'),
          btn('Configurar ahora', () => openConfigModal(root), { variant: 'secondary' }),
        ]),
      ]),
    ]),
  ]);
}

function openConfigModal(root) {
  openModal({
    title: 'Conectar Meta Ads API',
    subtitle: 'Pega tus credenciales — se guardan cifradas en el VPS.',
    fields: [
      { name: 'accessToken', label: 'Access Token (long-lived)', type: 'password', required: true },
      { name: 'adAccountId', label: 'Ad Account ID (act_XXXXXXX)', required: true },
    ],
    submitLabel: 'Guardar y verificar',
    onSubmit: async (data) => {
      toast('Verificando credenciales...', 'info', 30000);
      try {
        await apiClient.post('/api/v1/settings/integrations/meta-ads', data);
        toast('Meta Ads conectada ✓', 'success');
        renderMetaAds(root);
      } catch (e) {
        toast('Error: ' + (e?.message || 'Token inválido'), 'error');
      }
    },
  });
}
